import { Link } from "react-router-dom";
import StatusBadge from "@/components/base/StatusBadge";
import type { Story } from "@/mocks/stories";

type Variant = "lead" | "grid" | "row" | "compact";

type StoryCardProps = {
  story: Story;
  variant?: Variant;
  index?: number;
};

const MetaLine = ({ story, light = false }: { story: Story; light?: boolean }) => (
  <div
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs ${
      light ? "text-background-300" : "text-foreground-500"
    }`}
  >
    <span className="flex items-center gap-1">
      <i className="ri-fire-line text-accent-500" />
      热度 {story.heat}
    </span>
    <span className="hidden sm:inline">{story.author}</span>
    <span className="tabular-nums">{story.date}</span>
  </div>
);

function LeadCard({ story }: { story: Story }) {
  return (
    <article className="group relative overflow-hidden rounded-lg border border-background-300 bg-foreground-950">
      <Link to={`/news/${story.id}`} className="block">
        <div className="relative h-[300px] w-full overflow-hidden md:h-[420px]">
          <img
            src={story.cover}
            alt={`${story.title} - 事件封面`}
            title={`${story.title} 黑瓜爆料追踪`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/10" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-500 px-2.5 py-1 font-label text-xs font-medium text-background-50">
              头条追踪
            </span>
            <StatusBadge status={story.status} />
            <span className="rounded-full border border-background-50/30 px-2.5 py-1 font-label text-xs text-background-100">
              {story.category}
            </span>
          </div>
          <h3 className="mb-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
            {story.title}
          </h3>
          <p className="mb-4 line-clamp-2 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            {story.summary}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <MetaLine story={story} light />
            <span className="flex items-center gap-1 whitespace-nowrap font-label text-sm text-background-100">
              查看时间线
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

function GridCard({ story, index }: { story: Story; index: number }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/news/${story.id}`} className="flex h-full flex-col">
        <div className="relative h-44 w-full shrink-0 overflow-hidden bg-background-200 md:h-48">
          <img
            src={story.cover}
            alt={`${story.title} - 事件配图`}
            title={`${story.title} 黑瓜爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-md bg-foreground-950/85 px-2 py-1 font-label text-xs text-background-100">
            {story.category}
          </span>
          <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md bg-background-50/90 font-label text-xs font-bold text-foreground-800">
            {index + 1}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <div className="mb-2 flex items-center gap-2">
            <StatusBadge status={story.status} />
          </div>
          <h3 className="mb-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {story.title}
          </h3>
          <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
            {story.summary}
          </p>
          <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-3">
            {story.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-secondary-50 px-2 py-0.5 font-label text-xs text-secondary-700"
              >
                #{tag}
              </span>
            ))}
          </div>
          <div className="mt-3">
            <MetaLine story={story} />
          </div>
        </div>
      </Link>
    </article>
  );
}

function RowCard({ story }: { story: Story }) {
  return (
    <article className="group border-b border-background-200 py-5 last:border-b-0">
      <Link to={`/news/${story.id}`} className="flex flex-col gap-4 sm:flex-row">
        <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
          <img
            src={story.cover}
            alt={`${story.title} - 追踪配图`}
            title={`${story.title} 事件追踪`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={story.status} />
            <span className="rounded-md bg-background-200 px-2 py-0.5 font-label text-xs text-foreground-600">
              {story.category}
            </span>
          </div>
          <h3 className="mb-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {story.title}
          </h3>
          <p className="mb-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {story.summary}
          </p>
          <MetaLine story={story} />
        </div>
      </Link>
    </article>
  );
}

function CompactCard({ story, index }: { story: Story; index: number }) {
  return (
    <li className="border-b border-background-200 last:border-b-0">
      <Link
        to={`/news/${story.id}`}
        className="group flex items-start gap-3 py-3 transition-colors"
      >
        <span
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-label text-xs font-bold ${
            index < 3
              ? "bg-primary-500 text-background-50"
              : "bg-background-200 text-foreground-600"
          }`}
        >
          {index + 1}
        </span>
        <span className="min-w-0">
          <span className="block font-heading text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {story.title}
          </span>
          <span className="mt-1 flex items-center gap-2 font-label text-xs text-foreground-500">
            <i className="ri-fire-line text-accent-500" />
            {story.heat}
            <span className="text-foreground-400">{story.date}</span>
          </span>
        </span>
      </Link>
    </li>
  );
}

export default function StoryCard({ story, variant = "grid", index = 0 }: StoryCardProps) {
  if (variant === "lead") return <LeadCard story={story} />;
  if (variant === "row") return <RowCard story={story} />;
  if (variant === "compact") return <CompactCard story={story} index={index} />;
  return <GridCard story={story} index={index} />;
}